import "@fortawesome/fontawesome-free/css/all.min.css";
import { useState } from "react";

function Webwasa() {
  const [activeTab, setActiveTab] = useState("methodology");

  return (
    <div>
      {/* -- Banner sec ---- */}
      <div className="page-banner-area">
        <h2>Services</h2>
        <div className="breadcrumbs-services">
          <ul>
            <li>
              <a href="/">Home</a>
            </li>
            <li> Software Composition Analysis (SCA)</li>
          </ul>
        </div>
      </div>
      {/* -- Banner sec ---- */}

      {/* ---- About service section ----- */}
      <div className="about-services-wrap">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              {/* headings */}
              <div className="heading-in mb-5">
                <h1>Software Composition Analysis (SCA)</h1>
                <p>
                  <b className="sub-heading">Introduction</b>
                  <br />
                  Modern applications heavily rely on third-party libraries,
                  frameworks, and open-source components to speed up development
                  and innovation. However, these components can introduce hidden
                  security, licensing, and compliance risks if not properly
                  managed. Software Composition Analysis (SCA) helps
                  organizations identify, evaluate, and manage risks associated
                  with open-source and third-party software dependencies,
                  ensuring applications remain secure, compliant, and reliable
                  throughout their lifecycle.
                </p>
              </div>

              {/* Why It Matters */}
              <div className="service-card-heading">
                <h1>Why It Matters</h1>
              </div>
              <div className="services-cards-wp">
                {[
                  {
                    icon: "fa-arrows-down-to-people",
                    title: "Hidden Risks",
                    desc: "Most applications are built on 60–90% open-source components, which may contain known vulnerabilities.",
                  },
                  {
                    icon: "fa-database",
                    title: "Exploitable Flaws",
                    desc: "Attackers actively target outdated or unpatched libraries to gain entry.",
                  },
                  {
                    icon: "fa-shield-halved",
                    title: "Compliance Gaps",
                    desc: "Failure to track licenses can lead to legal and regulatory consequences.",
                  },
                  {
                    icon: "fa-table",
                    title: "Supply Chain Security",
                    desc: "Prevent risks introduced through third-party or open-source dependencies.",
                  },
                  {
                    icon: "fa-business-time",
                    title: "Cost Efficiency",
                    desc: "Proactively identifying vulnerable libraries avoids expensive remediation after release.",
                  },
                  {
                    icon: "fa-business-time",
                    title: "Customer Trust",
                    desc: "Secure, compliant software builds confidence and credibility with stakeholders.",
                  },
                ].map((item, i) => (
                  <div className="service-box" key={i}>
                    <div className="service-icon">
                      <i className={`fa-solid ${item.icon}`}></i>
                    </div>
                    <div className="service-card-content">
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div className="tab-pill-sec-main">
                <div className="tab-inner-contain">
                  <ul className="nav nav-pills">
                    {[
                      { id: "methodology", label: "Methodology" },
                      { id: "keybenefits", label: "Key Benefits" },
                      { id: "deliverables", label: "Deliverables" },
                    ].map((tab) => (
                      <li className="nav-item" key={tab.id}>
                        <button
                          className={`nav-link${activeTab === tab.id ? " active" : ""}`}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          {tab.label}
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="tab-content">
                    {/* Methodology */}
                    <div
                      className={`tab-pane fade${activeTab === "methodology" ? " show active" : ""}`}
                      role="tabpanel"
                    >
                      <p>Our SCA methodology integrates seamlessly into your Software Development Lifecycle (SDLC):</p>
                      <ul>
                        <li><b>Inventory Creation :-</b> Build a complete list of open-source and third-party components.</li>
                        <li><b>Automated Scanning :-</b> Detect known vulnerabilities, misconfigurations, and license issues using leading SCA tools.</li>
                        <li><b>Risk Assessment :-</b> Prioritize vulnerabilities based on severity, exploitability, and business impact.</li>
                        <li><b>Compliance Verification :-</b> Check license obligations (GPL, MIT, Apache, etc.) to avoid legal conflicts.</li>
                        <li><b>Remediation Guidance :-</b> Recommend patching, upgrading, or replacing vulnerable components.</li>
                        <li><b>Continuous Monitoring :-</b> Ongoing alerts for new vulnerabilities impacting your software supply chain.</li>
                      </ul>
                    </div>

                    {/* Key Benefits */}
                    <div
                      className={`tab-pane fade${activeTab === "keybenefits" ? " show active" : ""}`}
                      role="tabpanel"
                    >
                      <ul>
                        <li>Complete visibility into third-party and open-source usage.</li>
                        <li>Identify and remediate vulnerabilities before attackers exploit them.</li>
                        <li>Ensure compliance with licensing requirements.</li>
                        <li>Reduce supply chain risks and hidden liabilities.</li>
                        <li>Enable faster, safer software delivery in DevSecOps pipelines.</li>
                        <li>Improve long-term resilience and customer trust.</li>
                      </ul>
                    </div>

                    {/* Deliverables */}
                    <div
                      className={`tab-pane fade${activeTab === "deliverables" ? " show active" : ""}`}
                      role="tabpanel"
                    >
                      <p>At the end of an SCA engagement, you will receive:</p>
                      <ul>
                        <li><b>Comprehensive SCA Report :-</b> Inventory of open-source and third-party components.</li>
                        <li><b>Vulnerability Analysis :-</b> Identified risks with severity and exploitability ratings.</li>
                        <li><b>License Compliance Report :-</b> Legal obligations and risk exposure mapping.</li>
                        <li><b>Remediation Roadmap :-</b> Clear guidance on patching, upgrading, or replacing components.</li>
                        <li><b>Executive Summary :-</b> High-level overview for leadership and stakeholders.</li>
                        <li><b>Continuous Monitoring Setup (Optional) :-</b> Ongoing alerts for new vulnerabilities.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Our Approach */}
              <div className="our-approach-sec-main">
                <div className="container">
                  <h3>Our Approach</h3>
                  <p>
                    We don’t just deliver vulnerability lists — we provide actionable insights that strengthen your software supply chain security.
                  </p>
                  <ul>
                    <li><b>Holistic Analysis :-</b> Covering both security risks and license compliance.</li>
                    <li><b>Expert-Backed Review :-</b> Our team validates tool findings to eliminate false positives.</li>
                    <li><b>Seamless Integration :-</b> Embeds into CI/CD pipelines for DevSecOps-ready workflows.</li>
                    <li><b>Tailored Reporting :-</b> Business-friendly executive summaries + technical deep dives.</li>
                    <li><b>Global Standards Alignment :-</b> Following NIST, OWASP, and ISO guidelines.</li>
                    <li><b>Proven Success :-</b> Experience across industries including finance, healthcare, SaaS, and government.</li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      {/* ---- About service section ----- */}
    </div>
  );
}

export default Webwasa;
